import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Anchor, Moon, Sun } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";

const links = [
  { href: "/#chapter-i", label: "Navy", labelLg: "I · Navy" },
  { href: "/#chapter-ii", label: "Career", labelLg: "II · Career" },
  { href: "/#chapter-iii", label: "World", labelLg: "III · World" },
];

export default function Navigation() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const { theme, toggle } = useTheme();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-themed-90 border-b border-[var(--c-border)] safe-pt"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 lg:px-16 h-14 sm:h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-3 group min-w-0"
          data-testid="nav-home-link"
        >
          <span className="w-8 h-8 shrink-0 border border-[var(--c-accent)]/60 flex items-center justify-center text-[var(--c-accent)] group-hover:bg-[var(--c-accent)] group-hover:text-[var(--c-bg)] transition-colors">
            <Anchor size={14} strokeWidth={2} />
          </span>
          <span className="flex flex-col leading-tight min-w-0">
            <span className="overline text-[10px] sm:text-xs truncate">
              T. P. Field
            </span>
            <span className="font-serif-display text-[13px] sm:text-sm text-[var(--c-text)] tracking-wide truncate">
              <span className="hidden xs:inline">A Life · </span>1946 – Present
            </span>
          </span>
        </Link>

        <nav className="flex items-center gap-3 sm:gap-5 md:gap-8 shrink-0">
          {links.map((l) => (
            <a
              key={l.href}
              href={isHome ? l.href.replace("/", "") : l.href}
              className="brass-link text-[11px] sm:text-xs md:text-sm font-mono-plex uppercase tracking-[0.16em] sm:tracking-[0.18em] py-2"
              data-testid={`nav-${l.labelLg.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              <span className="sm:hidden">{l.label}</span>
              <span className="hidden sm:inline">{l.labelLg}</span>
            </a>
          ))}

          <button
            onClick={toggle}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            className="w-9 h-9 border border-[var(--c-border)] hover:border-[var(--c-accent)] flex items-center justify-center text-[var(--c-text)] hover:text-[var(--c-accent)] transition-colors"
            data-testid="theme-toggle-btn"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </nav>
      </div>
      <ScrollProgress />
    </header>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);

  useEffect(() => {
    let ticking = false;
    const apply = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setP(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        apply();
        ticking = false;
      });
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", apply);
    };
  }, []);

  return (
    <span
      className="scroll-progress"
      style={{ transform: `scaleX(${p})` }}
      aria-hidden="true"
      data-testid="scroll-progress"
    />
  );
}
